import {
  Table, Badge, Text, Group, TextInput, Title, Stack, ActionIcon, Avatar, Container,
  Loader, Center, Pagination, Button, Card, Box, Checkbox, Popover, Autocomplete, Select,
  SegmentedControl, ThemeIcon, Anchor,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { IconSearch, IconChevronRight, IconPlus, IconUpload, IconAlertTriangle, IconTrash, IconTag, IconLock, IconUsers, IconLayoutList, IconMap } from "@tabler/icons-react";
import InfoHint from "../components/InfoHint";
import FilterBar from "../components/FilterBar";
import OverflowActions from "../components/OverflowActions";
import ContactActions from "../components/ContactActions";
import { useIsMobile } from "../hooks/useIsMobile";
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { getCandidates, searchCandidates, getTagDefinitions, bulkUpdateCandidateTags, bulkDeleteCandidates, getProjects } from "../api";
import { CandidateAvatar } from "./CandidateAvatar";
import ExpandableTags from "./ExpandableTags";
import CandidateHoverCard from "../components/CandidateHoverCard";
import AddCandidateDialog from "../components/AddCandidateDialog";
import { useStore } from "../store";
import { usePlan } from "../hooks/usePlan";
import { requestUpgrade } from "../Pricing/upgrade";
import { formatCandidateName } from "../_Shared/candidateName";
import { candidateRoleAndCompany } from "../_Shared/candidateRole";
import CompanyLink from "../components/CompanyLink";
import { candidateLocation } from "../_Shared/candidateLocation";
import { relativeTime } from "../_Shared/time";

// Lazy: mapbox-gl (~400 KB gz, own vendor-map chunk) must cost nothing until the map is toggled.
const CandidatesMapView = lazy(() => import("./CandidatesMapView"));

const roleColors = { candidate: "blue", client: "green", reference: "violet", source: "orange", ident: "cyan", interestingPotential: "yellow" };
const allRoles = ["candidate", "client", "reference", "source", "ident", "interestingPotential"];
const stageColors = {
  identified: "gray",
  longlist: "blue",
  shortlist: "cyan",
  interview: "teal",
  finalist: "violet",
  placed: "green",
  rejected: "red",
};

export default function CandidateList() {
  const { t, i18n } = useTranslation("Veft");
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);
  const [consentFilter, setConsentFilter] = useState(null);
  const [openToWorkOnly, setOpenToWorkOnly] = useState(false);
  const [projects, setProjects] = useState([]);
  const [, setLocation] = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const { atCandidateLimit, candidates: candidateQuota, canBulkImport } = usePlan();
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [tagDefs, setTagDefs] = useState([]);
  const debounceRef = useRef(null);
  const pageSize = 50;

  const sortMode = useStore((s) => s.sortMode);
  const setSortMode = useStore((s) => s.setSortMode);
  const nameFormat = useStore((s) => s.nameFormat);
  const candidatesView = useStore((s) => s.candidatesView);
  const setCandidatesView = useStore((s) => s.setCandidatesView);
  const isMapView = candidatesView === "map";

  useEffect(() => {
    getTagDefinitions().then((d) => setTagDefs(Array.isArray(d) ? d : [])).catch(() => setTagDefs([]));
    getProjects().then((p) => setProjects(Array.isArray(p) ? p : [])).catch(() => setProjects([]));
  }, []);

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAllSelected() {
    setSelectedIds((prev) => {
      const allIds = candidates.map((c) => c.id);
      const allSelected = allIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allIds);
    });
  }

  async function applyBulkTag({ add, remove }) {
    if (selectedIds.size === 0) return;
    try {
      await bulkUpdateCandidateTags({
        candidateIds: [...selectedIds],
        ...(add ? { add: [add] } : {}),
        ...(remove ? { remove: [remove] } : {}),
      });
      notifications.show({ title: t("tags_updated"), message: t("tags_updated_msg", { count: selectedIds.size }), color: "green" });
      setSelectedIds(new Set());
      await loadCandidates();
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("error") });
    }
  }

  function handleBulkDelete() {
    const count = selectedIds.size;
    if (count === 0) return;
    const ids = [...selectedIds];
    modals.openConfirmModal({
      title: t("delete_selected_title"),
      centered: true,
      children: <Text size="sm">{t("delete_selected_confirm", { count })}</Text>,
      labels: { confirm: t("delete"), cancel: t("cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          const res = await bulkDeleteCandidates(ids);
          const deletedCount = res?.deletedCount ?? count;
          notifications.show({ title: t("deleted_notification"), message: t("contacts_deleted_msg", { count: deletedCount }), color: "green" });
          setSelectedIds(new Set());
          // Deleting a full page can leave the current page beyond the new last page — clamp so the
          // user lands on a page that still has rows, instead of the "empty database" get-started card.
          const newTotalPages = Math.max(1, Math.ceil(Math.max(0, totalCount - deletedCount) / pageSize));
          if (page > newTotalPages) setPage(newTotalPages); // effect reloads + clears selection
          else await loadCandidates();
        } catch {
          notifications.show({ color: "red", title: t("error"), message: t("error") });
        }
      },
    });
  }

  useEffect(() => {
    loadCandidates();
    setSelectedIds(new Set());
  }, [page, roleFilter, projectFilter, sortMode, consentFilter, openToWorkOnly]);

  async function loadCandidates() {
    setLoading(true);
    try {
      const sortParam = sortMode === "alpha" ? "alpha" : null;
      const consentOverdue = consentFilter === "overdue";
      const consentStatus = consentFilter && consentFilter !== "overdue" ? consentFilter : null;
      const data = await getCandidates(page, pageSize, roleFilter, projectFilter, sortParam, consentStatus, consentOverdue, openToWorkOnly);
      setCandidates(Array.isArray(data) ? data : data?.data ?? []);
      setTotalCount(data?.totalCount ?? (Array.isArray(data) ? data.length : 0));
    } catch (e) {
      console.error("Failed to load candidates", e);
    } finally {
      setLoading(false);
    }
  }

  function handleSearchChange(e) {
    const q = e.currentTarget.value;
    setSearch(q);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!q.trim()) {
      debounceRef.current = setTimeout(() => {
        setPage(1);
        loadCandidates();
      }, 100);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchCandidates(q);
        setCandidates(Array.isArray(results) ? results : []);
        setTotalCount(Array.isArray(results) ? results.length : 0);
      } catch (e) {
        console.error("Search failed", e);
      } finally {
        setLoading(false);
      }
    }, 300);
  }

  const isMobile = useIsMobile();
  const totalPages = Math.ceil(totalCount / pageSize);
  const activeFilterCount = [roleFilter, projectFilter, consentFilter, openToWorkOnly].filter(Boolean).length;

  // Coupled rendering: an alphabetically sorted list (by last name) always shows
  // "Last, First" so the order is visible, overriding the name-format preference.
  // Quick-search results are relevance-ordered, so they keep the normal format.
  const rowNameFormat = sortMode === "alpha" && !search.trim() ? "lastFirst" : nameFormat;

  return (
    <Container size="lg" py="md">
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>{t("candidates")}</Title>
          <Text size="sm" c="dimmed">{t("profiles_count", { count: totalCount })}</Text>
        </Group>
        <OverflowActions
          primary={{
            label: t("add"),
            icon: atCandidateLimit ? <IconLock size={16} /> : <IconPlus size={16} />,
            onClick: () =>
              atCandidateLimit
                ? requestUpgrade(`You've reached your plan's candidate limit (${candidateQuota.max}). Upgrade to add more candidates.`)
                : setCreateOpen(true),
          }}
          actions={[
            { label: t("import"), icon: <IconUpload size={16} />, onClick: () => setLocation("/candidates/import") },
            { label: t("trash"), icon: <IconTrash size={16} />, onClick: () => setLocation("/candidates/deleted") },
          ]}
        >
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="dimmed"
            onClick={() => setLocation("/candidates/deleted")}
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <IconTrash size={14} />
            {t("trash")}
          </Anchor>
          <Button variant="light" leftSection={<IconUpload size={16} />} size="sm" onClick={() => setLocation("/candidates/import")}>
            {t("import")}
          </Button>
          <Button
            leftSection={atCandidateLimit ? <IconLock size={16} /> : <IconPlus size={16} />}
            size="sm"
            onClick={() =>
              atCandidateLimit
                ? requestUpgrade(`You've reached your plan's candidate limit (${candidateQuota.max}). Upgrade to add more candidates.`)
                : setCreateOpen(true)
            }
          >
            {t("add")}
          </Button>
        </OverflowActions>
      </Group>

      {!isMapView && (
        <TextInput
          placeholder={t("search_candidates")}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={handleSearchChange}
        />
      )}

      <FilterBar
        activeCount={activeFilterCount}
        trailing={
          <Group gap={6} align="center" wrap="nowrap">
            <SegmentedControl
              size="xs"
              value={candidatesView}
              onChange={setCandidatesView}
              data={[
                { value: "list", label: <Center><IconLayoutList size={14} aria-label={t("view_list_aria")} /></Center> },
                { value: "map", label: <Center><IconMap size={14} aria-label={t("view_map_aria")} /></Center> },
              ]}
            />
            {!isMapView && (
              <>
                <Text size="xs" c="dimmed" visibleFrom="sm">{t("sort_label")}:</Text>
                <SegmentedControl
                  size="xs"
                  value={sortMode}
                  onChange={(v) => { setSortMode(v); setPage(1); }}
                  data={[
                    { value: "recent", label: t("sort_recent") },
                    { value: "alpha", label: t("sort_alpha") },
                  ]}
                />
              </>
            )}
          </Group>
        }
      >
        <Group gap="xs" wrap="wrap" align="center">
          <Badge
            size="sm"
            variant={roleFilter === null ? "filled" : "light"}
            style={{ cursor: "pointer" }}
            onClick={() => { setRoleFilter(null); setPage(1); }}
          >
            {t("all_filter")}
          </Badge>
          {allRoles.map((r) => (
            <Badge
              key={r}
              size="sm"
              color={roleColors[r]}
              variant={roleFilter === r ? "filled" : "dot"}
              style={{ cursor: "pointer" }}
              onClick={() => { setRoleFilter(roleFilter === r ? null : r); setPage(1); }}
            >
              {t(`role_${r}`)}
            </Badge>
          ))}
          <InfoHint i18nKey="roles_help" multiline width={300} />
        </Group>
        <Select
          size="xs"
          placeholder={t("filter_by_project")}
          data={projects.map((p) => ({ value: p.id, label: p.title }))}
          value={projectFilter}
          onChange={(v) => { setProjectFilter(v); setPage(1); }}
          searchable
          clearable
          style={{ minWidth: 220 }}
        />
        <Select
          size="xs"
          placeholder={t("filter_by_consent")}
          data={[
            { value: "notRequested", label: t("consent_status_notRequested") },
            { value: "requested", label: t("consent_status_requested") },
            { value: "overdue", label: t("consent_status_overdue") },
            { value: "granted", label: t("consent_status_granted") },
            { value: "declined", label: t("consent_status_declined") },
            { value: "expired", label: t("consent_status_expired") },
            { value: "withdrawn", label: t("consent_status_withdrawn") },
          ]}
          value={consentFilter}
          onChange={(v) => { setConsentFilter(v); setPage(1); }}
          clearable
          style={{ minWidth: 190 }}
        />
        <Badge
          size="sm"
          color="teal"
          variant={openToWorkOnly ? "filled" : "light"}
          style={{ cursor: "pointer" }}
          onClick={() => { setOpenToWorkOnly((v) => !v); setPage(1); }}
        >
          {t("open_to_work")}
        </Badge>
      </FilterBar>

      {!isMapView && selectedIds.size > 0 && (
        <BulkTagBar
          count={selectedIds.size}
          tagDefs={tagDefs}
          onApply={applyBulkTag}
          onDelete={handleBulkDelete}
          onClear={() => setSelectedIds(new Set())}
          t={t}
        />
      )}

      {isMapView ? (
        <Suspense fallback={<Center py="xl"><Loader /></Center>}>
          <CandidatesMapView
            role={roleFilter}
            projectId={projectFilter}
            consentFilter={consentFilter}
            openToWork={openToWorkOnly}
          />
        </Suspense>
      ) : loading ? (
        <Center py="xl"><Loader /></Center>
      ) : candidates.length === 0 ? (
        // Distinguish "no matches for this search/filter" from a genuinely empty database —
        // only the latter gets the action-oriented get-started card.
        (search.trim() || roleFilter || projectFilter || consentFilter || openToWorkOnly) ? (
          <Text c="dimmed" ta="center" py="xl">{t("no_candidates")}</Text>
        ) : (
          <Card withBorder p="xl" maw={460} mx="auto" mt="md">
            <Stack gap="sm" align="center" ta="center">
              <ThemeIcon variant="light" size="xl" radius="xl"><IconUsers size={24} /></ThemeIcon>
              <Text fw={600}>{t("contacts_empty_title")}</Text>
              <Text size="sm" c="dimmed">{t("contacts_empty_help")}</Text>
              <Group gap="xs" mt="xs">
                <Button
                  leftSection={atCandidateLimit ? <IconLock size={16} /> : <IconPlus size={16} />}
                  onClick={() =>
                    atCandidateLimit
                      ? requestUpgrade(`You've reached your plan's candidate limit (${candidateQuota.max}). Upgrade to add more candidates.`)
                      : setCreateOpen(true)
                  }
                >
                  {t("add")}
                </Button>
                <Button variant="light" leftSection={<IconUpload size={16} />} onClick={() => setLocation("/candidates/import")}>
                  {t("import")}
                </Button>
              </Group>
            </Stack>
          </Card>
        )
      ) : (
        <>
          {isMobile ? (
            <Stack gap="xs">
              {candidates.map((c) => {
                const { role, company, companyId, isFormer } = candidateRoleAndCompany(c);
                const roleLabel = isFormer && role ? `${t("role_former_prefix")} ${role}` : role;
                return (
                <Card
                  key={c.id}
                  p="sm"
                  radius="sm"
                  withBorder
                  style={{ cursor: "pointer" }}
                  onClick={() => setLocation(`/candidates/${c.id}`)}
                >
                  <Group gap="sm" wrap="nowrap">
                    <Checkbox
                      size="xs"
                      aria-label={t("select_row")}
                      checked={selectedIds.has(c.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelected(c.id)}
                      style={{ flexShrink: 0 }}
                    />
                    <CandidateHoverCard candidate={c}>
                      <CandidateAvatar candidate={c} size="sm" radius="xl" color="blue" style={{ flexShrink: 0 }} />
                    </CandidateHoverCard>
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={500} truncate>{formatCandidateName(c, rowNameFormat)}</Text>
                      {role ? (
                        <Text size="xs" c="dimmed" truncate>
                          {roleLabel}{company ? " · " : ""}
                          <CompanyLink company={company} companyId={companyId} />
                        </Text>
                      ) : c.enrichmentStatus && c.enrichmentStatus !== "none" && c.enrichmentStatus !== "complete" ? (
                        <Badge size="xs" variant="light" color={c.enrichmentStatus === "failed" ? "red" : "blue"}
                          leftSection={c.enrichmentStatus === "failed" ? <IconAlertTriangle size={10} /> : <Loader size={10} />}
                        >
                          {c.enrichmentStatus === "failed" ? t("enrichment_failed") : t("enriching")}
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">{t("no_role_info")}</Text>
                      )}
                      {candidateLocation(c) && <Text size="xs" c="dimmed">{candidateLocation(c)}</Text>}
                      {/* Phone as tap-to-call — the single most useful action on a phone, and the
                          field Georgia flagged as missing from the list. */}
                      <ContactActions contact={c} size="xs" mt={4} />
                      {c.updatedAt && (
                        <Text size="xs" c="dimmed">{t("updated_ago", { ago: relativeTime(c.updatedAt, i18n.language) })}</Text>
                      )}
                      {c.openToWork && (
                        <Badge size="xs" color="teal" variant="light" mt={4}>{t("open_to_work")}</Badge>
                      )}
                    </Box>
                    <ActionIcon variant="subtle" size="sm" style={{ flexShrink: 0 }}>
                      <IconChevronRight size={14} />
                    </ActionIcon>
                  </Group>
                  {(c.projectLinks ?? []).length > 0 && (
                    <Group gap={4} mt={6}>
                      {(c.projectLinks ?? []).map((link) => (
                        <Badge
                          key={`${link.projectId}-${link.stage}`}
                          size="xs"
                          variant="light"
                          color={stageColors[link.stage] ?? "gray"}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setProjectFilter(link.projectId);
                            setPage(1);
                          }}
                        >
                          {link.projectTitle} → {t(`stage_${link.stage}`)}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  {(c.tags ?? []).length > 0 && (
                    <Box mt={6}>
                      <ExpandableTags tags={c.tags} limit={4} />
                    </Box>
                  )}
                </Card>
                );
              })}
            </Stack>
          ) : (
            <Table.ScrollContainer minWidth={600}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 32 }}>
                      <Checkbox
                        size="xs"
                        aria-label={t("select_all")}
                        checked={candidates.length > 0 && candidates.every((c) => selectedIds.has(c.id))}
                        indeterminate={selectedIds.size > 0 && !candidates.every((c) => selectedIds.has(c.id))}
                        onChange={toggleAllSelected}
                      />
                    </Table.Th>
                    <Table.Th>{t("name")}</Table.Th>
                    <Table.Th>{t("current_role")}</Table.Th>
                    <Table.Th>{t("projects")}</Table.Th>
                    <Table.Th>{t("tags")}</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {candidates.map((c) => {
                    const { role, company, companyId, isFormer } = candidateRoleAndCompany(c);
                    const roleLabel = isFormer && role ? `${t("role_former_prefix")} ${role}` : role;
                    return (
                    <Table.Tr
                      key={c.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setLocation(`/candidates/${c.id}`)}
                    >
                      <Table.Td onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                        <Checkbox
                          size="xs"
                          aria-label={t("select_row")}
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleSelected(c.id)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap="sm" wrap="nowrap">
                          <CandidateHoverCard candidate={c}>
                            <CandidateAvatar candidate={c} size="sm" radius="xl" color="blue" style={{ flexShrink: 0 }} />
                          </CandidateHoverCard>
                          <div>
                            <Text size="sm" fw={500}>{formatCandidateName(c, rowNameFormat)}</Text>
                            <Text size="xs" c="dimmed">{candidateLocation(c)}</Text>
                            {c.updatedAt && (
                              <Text size="xs" c="dimmed">{t("updated_ago", { ago: relativeTime(c.updatedAt, i18n.language) })}</Text>
                            )}
                            {c.openToWork && (
                              <Badge size="xs" color="teal" variant="light" mt={2}>{t("open_to_work")}</Badge>
                            )}
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {role ? (
                          <>
                            <Text size="sm">{roleLabel}</Text>
                            <Text size="xs" c="dimmed">
                              <CompanyLink company={company} companyId={companyId} />
                            </Text>
                          </>
                        ) : c.enrichmentStatus && c.enrichmentStatus !== "none" && c.enrichmentStatus !== "complete" ? (
                          <Badge size="xs" variant="light" color={c.enrichmentStatus === "failed" ? "red" : "blue"}
                            leftSection={c.enrichmentStatus === "failed" ? <IconAlertTriangle size={10} /> : <Loader size={10} />}
                          >
                            {c.enrichmentStatus === "failed" ? t("enrichment_failed") : t("enriching")}
                          </Badge>
                        ) : (
                          <Text size="xs" c="dimmed">{t("no_role_info")}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="wrap">
                          {(c.projectLinks ?? []).map((link) => (
                            <Badge
                              key={`${link.projectId}-${link.stage}`}
                              size="xs"
                              variant="light"
                              color={stageColors[link.stage] ?? "gray"}
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setProjectFilter(link.projectId);
                                setPage(1);
                              }}
                            >
                              {link.projectTitle} → {t(`stage_${link.stage}`)}
                            </Badge>
                          ))}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <ExpandableTags tags={c.tags} limit={3} />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon variant="subtle" size="sm">
                          <IconChevronRight size={14} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}

          {!search && totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </>
      )}

      <AddCandidateDialog
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => loadCandidates()}
      />
    </Stack>
    </Container>
  );
}

function BulkTagBar({ count, tagDefs, onApply, onDelete, onClear, t }) {
  const [mode, setMode] = useState(null); // "add" | "remove" | null
  const [tagInput, setTagInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tagOptions = tagDefs.map((td) => td.name);

  function close() {
    setMode(null);
    setTagInput("");
  }

  async function submit() {
    const tag = tagInput.trim();
    if (!tag) return;
    setSubmitting(true);
    try {
      await onApply(mode === "add" ? { add: tag } : { remove: tag });
      close();
    } finally {
      setSubmitting(false);
    }
  }

  function renderPopover(activeMode, label, color, placeholder) {
    return (
      <Popover opened={mode === activeMode} onClose={close} position="bottom-start" shadow="md" withinPortal>
        <Popover.Target>
          <Button size="xs" variant="light" color={color}
            leftSection={activeMode === "add" ? <IconTag size={14} /> : null}
            onClick={() => { setMode(activeMode); setTagInput(""); }}>
            {label}
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs">
            <Autocomplete
              size="xs"
              data={tagOptions}
              value={tagInput}
              onChange={setTagInput}
              placeholder={placeholder}
              data-autofocus
            />
            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="default" onClick={close}>{t("cancel")}</Button>
              <Button size="xs" color={color} loading={submitting} disabled={!tagInput.trim()} onClick={submit}>
                {t("apply")}
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    );
  }

  return (
    <Card p="xs" withBorder bg="blue.0">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <Text size="sm" fw={500}>{t("selected_count", { count })}</Text>
          {renderPopover("add", t("bulk_add_tag"), undefined, t("add_tag"))}
          {renderPopover("remove", t("bulk_remove_tag"), "red", t("tag_to_remove"))}
        </Group>
        <Group gap="xs">
          <Button size="xs" variant="filled" color="red" leftSection={<IconTrash size={14} />} onClick={onDelete}>
            {t("bulk_delete")}
          </Button>
          <Button size="xs" variant="subtle" color="gray" onClick={onClear}>{t("clear_selection")}</Button>
        </Group>
      </Group>
    </Card>
  );
}
